import { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  percentagePointChange,
  previousPeriod,
  relativeChange,
} from "./analytics-comparison";

export interface DataAvailability {
  requested_start?: string | null;
  requested_end?: string | null;
  overall_min?: string | null;
  overall_max?: string | null;
  overall_count?: number;
  filtered_min?: string | null;
  filtered_max?: string | null;
  filtered_count?: number;
  campaign?: string | null;
  campaign_min?: string | null;
  campaign_max?: string | null;
  campaign_count?: number;
}

export interface AnalyticsData {
  kpis: {
    totalCalls: number;
    totalCallsDelta: number;
    successRate: number;
    successRateDelta: number;
    avgDurationSec: number;
    avgDurationDelta: number;
    connectedCalls: number;
    connectedCallsDelta: number;
    creditsUsed: number;
    creditsBilledCalls: number;
    creditsRecomputedCalls: number;
    creditsPulseSeconds: number;
    uniqueContacts: number;
    retryCalls: number;
    uniqueReached: number;
    uniqueNotReached: number;
    callsFailed: number;
    uniqueConnectivityPercentage: number;
    connectivityPercentage: number;
    durationBreakdown?: any;
  };
  callsPerDay: { day: string; calls: number }[];
  outcomes: { name: string; value: number; color: string }[];
  hourlyPickup: {
    window: string;
    pickup: number;
    attempted: number;
    connected: number;
    best?: boolean;
  }[];
  timeWindowTimezone: string;
  dataAvailability: DataAvailability | null;
  recentExports: {
    id: string;
    name: string;
    status: "ready" | "building";
    createdAt: string;
  }[];
}

const EMPTY_ANALYTICS: AnalyticsData = {
  kpis: {
    totalCalls: 0,
    totalCallsDelta: 0,
    successRate: 0,
    successRateDelta: 0,
    avgDurationSec: 0,
    avgDurationDelta: 0,
    connectedCalls: 0,
    connectedCallsDelta: 0,
    creditsUsed: 0,
    creditsBilledCalls: 0,
    creditsRecomputedCalls: 0,
    creditsPulseSeconds: 60,
    uniqueContacts: 0,
    retryCalls: 0,
    uniqueReached: 0,
    uniqueNotReached: 0,
    callsFailed: 0,
    uniqueConnectivityPercentage: 0,
    connectivityPercentage: 0,
  },
  callsPerDay: [],
  outcomes: [
    { name: "Completed", value: 0, color: "#2b8f52" },
    { name: "Failed", value: 0, color: "#d92d20" },
    { name: "No Answer", value: 0, color: "#a8a099" },
    { name: "Voicemail", value: 0, color: "#d99418" },
  ],
  hourlyPickup: [],
  timeWindowTimezone: "IST",
  dataAvailability: null,
  recentExports: [],
};

export interface AgentMeta {
  agent_id: string;
  agent_name: string;
  agent_type?: "single" | "multi" | string;
  multi_agent_config_id?: string | null;
  status?: string;
  current_version?: string;
}

interface AnalyticsSnapshot {
  raw: any;
  meta: AgentMeta | null;
  availability: DataAvailability | null;
}

function numericValue(...values: unknown[]): number {
  for (const value of values) {
    if (value === null || value === undefined || value === "") continue;
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
}

function formatHour(hour: number) {
  return `${String(hour).padStart(2, "0")}:00`;
}

function mapTimeWindows(raw: any): AnalyticsData["hourlyPickup"] {
  const rows = Array.isArray(raw.calls_by_time_window)
    ? raw.calls_by_time_window
    : [];
  const byStartHour = new Map<number, { attempted: number; connected: number }>();

  for (const row of rows) {
    const startHour = numericValue(row?.start_hour);
    if (!Number.isInteger(startHour) || startHour < 0 || startHour > 21) continue;
    byStartHour.set(startHour, {
      attempted: numericValue(row?.attempted),
      connected: numericValue(row?.connected),
    });
  }

  const windows = Array.from({ length: 8 }, (_, index) => {
    const startHour = index * 3;
    const counts = byStartHour.get(startHour) || { attempted: 0, connected: 0 };
    return {
      window: `${formatHour(startHour)}-${formatHour(startHour + 3)}`,
      attempted: counts.attempted,
      connected: counts.connected,
      pickup: counts.attempted
        ? Math.round((counts.connected / counts.attempted) * 1000) / 10
        : 0,
    };
  });

  const best = windows
    .filter((window) => window.attempted > 0)
    .sort((left, right) =>
      right.pickup - left.pickup
      || right.attempted - left.attempted
      || left.window.localeCompare(right.window)
    )[0];

  return windows.map((window) => ({
    ...window,
    best: Boolean(best && window.window === best.window),
  }));
}

function mapRawToAnalytics(raw: any, availability: DataAvailability | null = null): AnalyticsData {
  const totalCalls = raw.total_calls || 0;
  const connected = raw.calls_connected || 0;
  const failed = raw.calls_failed || 0;
  const voicemail =
    raw.voicemail_detected || raw.call_status_distribution?.voicemail || 0;
  // Voicemail calls are also "connected"; the pie needs non-overlapping slices.
  const completedSlice = raw.call_status_distribution?.completed ?? connected;
  const noAnswer = totalCalls - (completedSlice + failed + voicemail);

  let callsPerDay: AnalyticsData["callsPerDay"] = [];
  if (raw.calls_by_day) {
    const dayArray = Array.isArray(raw.calls_by_day)
      ? raw.calls_by_day
      : Object.values(raw.calls_by_day);
    callsPerDay = dayArray.map((day: any) => ({
      day: day.date ? day.date.split("-").slice(1).join("/") : "?",
      calls: day.attempted || 0,
    }));
  }

  return {
    ...EMPTY_ANALYTICS,
    kpis: {
      ...EMPTY_ANALYTICS.kpis,
      totalCalls,
      successRate: raw.connectivity_percentage || 0,
      avgDurationSec: raw.avg_duration_seconds || 0,
      connectedCalls: connected,
      creditsUsed: raw.credits_used || 0,
      creditsBilledCalls: raw.credits_billed_calls || 0,
      creditsRecomputedCalls: raw.credits_recomputed_calls || 0,
      creditsPulseSeconds: raw.credits_pulse_seconds || 60,
      uniqueContacts: raw.unique_contacts || raw.uniqueContacts || 0,
      retryCalls: raw.retry_calls || raw.retryCalls || 0,
      uniqueReached: raw.unique_reached || raw.uniqueReached || 0,
      uniqueNotReached: raw.unique_not_reached || raw.uniqueNotReached || 0,
      callsFailed: failed,
      uniqueConnectivityPercentage: raw.unique_connectivity_percentage || raw.uniqueConnectivityPercentage || 0,
      connectivityPercentage: raw.connectivity_percentage || raw.connectivityPercentage || 0,
      durationBreakdown: raw.duration_breakdown || raw.durationBreakdown || null,
    },
    callsPerDay,
    outcomes: [
      { name: "Completed", value: completedSlice, color: "#2b8f52" },
      { name: "Failed", value: failed, color: "#d92d20" },
      {
        name: "No Answer",
        value: Math.max(0, noAnswer),
        color: "#a8a099",
      },
      { name: "Voicemail", value: voicemail, color: "#d99418" },
    ],
    hourlyPickup: mapTimeWindows(raw),
    timeWindowTimezone: raw.time_window_timezone === "Asia/Kolkata"
      ? "IST"
      : raw.time_window_timezone || "IST",
    dataAvailability: availability,
  };
}

function formatDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function useAnalytics(
  startDate: Date,
  endDate: Date,
  filters: any = {},
) {
  const { token, user } = useAuth();
  const [data, setData] = useState<AnalyticsData>(EMPTY_ANALYTICS);
  const [loading, setLoading] = useState(true);
  const [agentMeta, setAgentMeta] = useState<AgentMeta | null>(null);

  const filtersKey = JSON.stringify(filters);

  useEffect(() => {
    let cancelled = false;
    const abortController = new AbortController();

    if (!token) {
      setLoading(false);
      setData(EMPTY_ANALYTICS);
      setAgentMeta(null);
      return undefined;
    }

    /**
     * Fetch analytics for a given period.
     * Makes a single request to FastAPI's StreamingResponse endpoint and reads
     * streamed NDJSON chunks, updating live UI progress as data is processed.
     */
    const fetchSnapshot = async (
      periodStart: Date,
      periodEnd: Date,
      onProgress?: (snapshot: AnalyticsSnapshot) => void,
    ): Promise<AnalyticsSnapshot> => {
      const apiUrl = process.env.REACT_APP_BACKEND_URL || "";
      const queryParams: Record<string, string> = {
        start_date: formatDate(periodStart),
        end_date: formatDate(periodEnd),
        stream: "true",
      };

      for (const [k, v] of Object.entries(filters || {})) {
        if (v !== undefined && v !== null && v !== "") {
          queryParams[k] = String(v);
        }
      }

      if (filters?.max_duration && !filters?.duration_operator) {
        queryParams.duration_operator = ">=";
      }

      const qs = new URLSearchParams(queryParams).toString();
      const response = await fetch(`${apiUrl}/api/v2/analytics/summary?${qs}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/x-ndjson, application/json",
        },
        signal: abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Analytics fetch failed: ${response.status} ${response.statusText}`);
      }

      const contentType = response.headers.get("content-type") || "";

      // Streamed NDJSON response
      if (contentType.includes("ndjson") || response.body) {
        const reader = response.body?.getReader();
        if (reader) {
          const decoder = new TextDecoder("utf-8");
          let buffer = "";
          let latestSnapshot: AnalyticsSnapshot | null = null;

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split("\n");
            buffer = lines.pop() || "";

            for (const line of lines) {
              const trimmed = line.trim();
              if (!trimmed) continue;
              try {
                const msg = JSON.parse(trimmed);
                const raw = msg.analytics || msg;
                const meta = msg.workflow || msg.agent || null;
                const availability = msg.data_availability || msg.dataAvailability || null;
                latestSnapshot = { raw, meta, availability };
                if (onProgress && !cancelled) {
                  onProgress(latestSnapshot);
                }
              } catch (e) {
                console.warn("Failed parsing streaming analytics line", e);
              }
            }
          }

          if (buffer.trim()) {
            try {
              const msg = JSON.parse(buffer.trim());
              const raw = msg.analytics || msg;
              const meta = msg.workflow || msg.agent || null;
              const availability = msg.data_availability || msg.dataAvailability || null;
              latestSnapshot = { raw, meta, availability };
              if (onProgress && !cancelled) {
                onProgress(latestSnapshot);
              }
            } catch (e) {
              // ignore trailing buffer parse error
            }
          }

          if (latestSnapshot) {
            return latestSnapshot;
          }
        }
      }

      // Fallback for standard non-streaming JSON
      const payload = await response.json();
      return {
        raw: payload.analytics || payload,
        meta: payload.workflow || payload.agent || null,
        availability: payload.data_availability || payload.dataAvailability || null,
      };
    };

    const fetchAnalytics = async () => {
      try {
        setLoading(true);
        setData(EMPTY_ANALYTICS);
        setAgentMeta(null);

        const prior = previousPeriod(startDate, endDate);

        // Fetch previous-period analytics concurrently in the background as a single request
        const previousSnapshotPromise = fetchSnapshot(
          prior.start,
          prior.end,
        ).catch((error) => {
          if (!abortController.signal.aborted) {
            console.error("Failed to fetch previous-period analytics", error);
          }
          return { raw: {}, meta: null, availability: null } as AnalyticsSnapshot;
        });

        // Current period: single streaming request, updating data live as chunks arrive
        const currentSnapshot = await fetchSnapshot(
          startDate,
          endDate,
          (progressSnap) => {
            if (cancelled) return;
            const live = mapRawToAnalytics(progressSnap.raw, progressSnap.availability);
            setData(live);
            setAgentMeta(progressSnap.meta);
            setLoading(false);
          },
        );

        if (cancelled) return;

        const current = mapRawToAnalytics(currentSnapshot.raw, currentSnapshot.availability);
        setAgentMeta(currentSnapshot.meta);
        setData(current);
        setLoading(false);

        const previousSnapshot = await previousSnapshotPromise;
        if (cancelled) return;
        const previous = mapRawToAnalytics(previousSnapshot.raw);
        setData({
          ...current,
          kpis: {
            ...current.kpis,
            totalCallsDelta: relativeChange(
              current.kpis.totalCalls,
              previous.kpis.totalCalls,
            ),
            successRateDelta: percentagePointChange(
              current.kpis.successRate,
              previous.kpis.successRate,
            ),
            avgDurationDelta: relativeChange(
              current.kpis.avgDurationSec,
              previous.kpis.avgDurationSec,
            ),
            connectedCallsDelta: relativeChange(
              current.kpis.connectedCalls,
              previous.kpis.connectedCalls,
            ),
          },
        });
      } catch (error: any) {
        if (!abortController.signal.aborted && !cancelled) {
          console.error("Failed to fetch analytics", error);
          setData(EMPTY_ANALYTICS);
          setAgentMeta(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchAnalytics();
    return () => {
      cancelled = true;
      abortController.abort();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, user, startDate, endDate, filtersKey]);

  return { data, loading, agentMeta };
}
